import React from 'react';
import { Sparkles, Tag, ArrowUpRight, Cloud } from 'lucide-react';
import { AskAiSection, ECOSYSTEM_LOGO_SLOT_CLASS, buildAskAiPrompt, buildAskAiProviders, ecosystemLogoClass } from '@rehub/ask-ai';
import { PHOTOCLEAR_FEATURES, PHOTOCLEAR_TAGLINE, PHOTOCLEAR_VIDEO } from '../constants';
import BeforeAfterSlider from './BeforeAfterSlider';
import StylePickerShowcase from './StylePickerShowcase';

interface LandingPageProps {
  onSignUp: () => void;
  onLogin: () => void;
  onOpenPricing?: () => void;
}

const REHUB_APPS = [
  {
    name: 'PhotoEdit',
    tagline: 'AI Photo Editing',
    description: 'Professional AI photo editing — tone, colour, detail and crop for every listing image.',
    url: 'https://photoedit.rehub.studio',
    logo: 'https://photoedit.rehub.studio/photoedit-logo.png',
  },
  {
    name: 'HDR Photo',
    tagline: 'AI HDR Merging',
    description: 'Merge bracketed exposures into studio-quality HDR, then enhance automatically.',
    url: 'https://hdrphoto.rehub.studio',
    logo: 'https://hdrphoto.rehub.studio/hdr-photo-logo.png',
  },
  {
    name: 'Photoclear.ai',
    tagline: 'AI Decluttering',
    description: 'Remove clutter and distractions instantly with AI to reveal clean, buyer-ready rooms.',
    url: 'https://photoclear.rehub.studio',
    logo: '/Photoclear Logo HR Trans.png',
    current: true,
  },
  {
    name: 'Virtual Staging',
    tagline: 'AI Virtual Staging',
    description: 'Transform empty rooms into beautifully furnished interiors in minutes.',
    url: 'https://staging.rehub.studio',
    logo: 'https://staging.rehub.studio/virtual-staging-logo-transparent.png',
  },
  {
    name: 'ImageMotion',
    tagline: 'AI Motion',
    description: 'Turn static listing photos into cinematic property videos automatically.',
    url: 'https://imagemotion.rehub.studio',
    logo: 'https://imagemotion.rehub.studio/Logo.webp',
  },
  {
    name: 'VideoMotion',
    tagline: 'AI Video Editing',
    description: 'Turn raw walkthrough footage into polished, social-ready video edits.',
    url: 'https://videomotion.rehub.studio',
    logo: 'https://videomotion.rehub.studio/logo.png',
  },
  {
    name: 'PropertyCuts',
    tagline: 'Video Assembler',
    description: 'Stitch photos, clips and floor plans into a finished property video.',
    url: 'https://propertycuts.rehub.studio',
    logo: 'https://propertycuts.rehub.studio/logo.png',
  },
  {
    name: 'Hyper Dusk',
    tagline: 'AI Day-to-Dusk',
    description: 'Transform daytime exteriors into stunning dusk shots with AI-powered lighting.',
    url: 'https://hyperdusk.rehub.studio',
    logo: 'https://hyperdusk.rehub.studio/hyperdusk-logo-wordmark-alpha.png',
  },
  {
    name: 'Screen Record',
    tagline: 'Guided Recordings',
    description: 'Record guided 360° tours and screen walkthroughs with voice, camera and a shareable link.',
    url: 'https://record.rehub.studio',
    logo: '/screenrecord-logo.png',
  },
  {
    name: '360 Tours',
    tagline: 'Virtual Tours',
    description: 'Build immersive virtual tours for property listings and developments.',
    url: 'https://360tours.rehub.studio',
    logo: 'https://360tours.rehub.studio/360tours-studio-logo-transparent.png',
  },
  {
    name: 'Planos',
    tagline: 'Interactive Floor Plans',
    description: 'Create interactive floor plans that let buyers explore properties visually.',
    url: 'https://planos.rehub.studio',
    logo: 'https://planos.rehub.studio/Planos%20Logo%20Red.png',
  },
  {
    name: 'Sketchplan',
    tagline: 'AI Floor Plans',
    description: 'Turn rough sketches into accurate, professional floor plans.',
    url: 'https://sketchplan.rehub.studio',
    logo: 'https://sketchplan.rehub.studio/cropped-Sketchplan-Logo1-removebg-preview.webp',
  },
];

const ASK_AI_PRODUCT = 'PhotoClear';
const ASK_AI_PROVIDERS = buildAskAiProviders(ASK_AI_PRODUCT);
const ASK_AI_PROMPT = buildAskAiPrompt({
  productName: ASK_AI_PRODUCT,
  siteUrl: 'https://photoclear.rehub.studio',
  askAiUrl: 'https://photoclear.ai/ask-ai',
  extraFocus:
    'Focus on AI photo decluttering and object removal for property listings — clutter, furniture, cars, bins, people and cables — plus credit pricing.',
});

const BEFORE_AFTER_EXAMPLES = [
  { before: '/gallery/kitchen-before.png', after: '/gallery/kitchen-after.png', caption: 'Kitchen — worktops cleared' },
  { before: '/gallery/living-before.png', after: '/gallery/living-after.png', caption: 'Living room — decluttered' },
  { before: '/gallery/bedroom-before.png', after: '/gallery/bedroom-after.png', caption: 'Bedroom — clutter removed' },
  { before: '/gallery/bathroom-before.png', after: '/gallery/bathroom-after.png', caption: 'Bathroom — clutter removed' },
  { before: '/gallery/exterior-before.png', after: '/gallery/exterior-after.png', caption: 'Exterior — bins, car & clutter removed' },
  { before: '/gallery/kitchen2-before.png', after: '/gallery/kitchen2-after.png', caption: 'Kitchen diner — clutter removed' },
];

const LandingPage: React.FC<LandingPageProps> = ({ onSignUp, onLogin, onOpenPricing }) => {
  return (
    <main className="relative z-10">
      <div className="container mx-auto px-4 py-16">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-violet-900/30 backdrop-blur-sm border border-violet-500/20 text-violet-300 rounded-full text-sm font-medium mb-8">
            <Sparkles size={14} className="shrink-0" />
            AI-Powered Photo Enhancement
          </div>
          {/* Logo instead of text */}
          <div className="flex justify-center mb-6">
            <img
              src="/Photoclear Logo HR Trans.png"
              alt="PhotoClear"
              className="h-24 md:h-28 w-auto object-contain drop-shadow-[0_0_24px_rgba(255,255,255,0.12)]"
            />
          </div>
          <p className="text-xl text-slate-300 mb-10 max-w-2xl mx-auto">
            {PHOTOCLEAR_TAGLINE}
          </p>
          {/* Buttons with strong contrast so they stay visible on fluid background */}
          <div className="flex flex-wrap items-center justify-center gap-4">
            <button
              onClick={onSignUp}
              className="group flex items-center gap-3 px-10 py-5 bg-gradient-to-r from-blue-600 to-cyan-500 hover:brightness-110 text-white rounded-2xl font-semibold text-lg transition-all shadow-xl shadow-blue-900/40 border border-blue-400/30"
            >
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-blue-600 group-hover:rotate-[360deg] transition-transform duration-700">
                <Sparkles size={20} strokeWidth={2} />
              </span>
              Try PhotoClear Free
            </button>
            <button
              onClick={onLogin}
              className="px-8 py-5 bg-slate-900/80 backdrop-blur-sm border-2 border-slate-500 hover:border-slate-400 text-slate-200 hover:text-white rounded-2xl font-semibold text-lg transition-all shadow-lg"
            >
              Sign In
            </button>
            {onOpenPricing && (
              <button
                onClick={onOpenPricing}
                className="inline-flex items-center gap-2 px-6 py-5 bg-teal-600 hover:bg-teal-500 text-white rounded-2xl font-medium text-base transition-all shadow-lg"
              >
                <Tag size={18} />
                Pricing & benefits
              </button>
            )}
          </div>
          <p className="text-sm text-slate-400 mt-4">
            10 free credits on sign-up. No card required.
          </p>

          <div className="mt-12 w-full max-w-3xl mx-auto rounded-2xl overflow-hidden border border-slate-700/80 shadow-2xl shadow-orange-900/20">
            <div className="relative w-full pt-[56.25%] bg-black">
              <iframe
                src={PHOTOCLEAR_VIDEO.embedUrl}
                title={PHOTOCLEAR_VIDEO.title}
                className="absolute inset-0 w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                referrerPolicy="strict-origin-when-cross-origin"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-16 text-left">
            {PHOTOCLEAR_FEATURES.map((feature) => (
              <div
                key={feature.title}
                className="p-6 bg-slate-900/70 backdrop-blur-md border border-slate-700/80 rounded-2xl hover:border-slate-600 transition-colors"
              >
                <div className="flex items-center gap-2 mb-3">
                  <Sparkles size={18} className="text-orange-400 shrink-0" />
                  <h3 className="text-lg font-semibold text-slate-100">{feature.title}</h3>
                </div>
                <p className="text-sm text-slate-400">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="max-w-4xl mx-auto mt-24 overflow-x-clip">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-sky-900/30 backdrop-blur-sm border border-sky-500/20 text-sky-300 rounded-full text-sm font-medium mb-4">
              <Cloud size={14} className="shrink-0" />
              Style pickers
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-100 mb-3">Skies, flowers and plants</h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              Blue sky and sunset fans, plus flower and plant circles — the same controls you use when editing a listing photo.
            </p>
          </div>
          <StylePickerShowcase autoPlay />
          <div className="flex justify-center mt-8">
            <a
              href="/styles"
              className="inline-flex items-center gap-2 text-sm font-medium text-sky-300 hover:text-sky-200 transition-colors"
            >
              Open the full picker page
              <ArrowUpRight size={16} />
            </a>
          </div>
        </div>

        {/* Before & After examples */}
        <div className="max-w-6xl mx-auto mt-24">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-orange-900/30 backdrop-blur-sm border border-orange-500/20 text-orange-300 rounded-full text-sm font-medium mb-4">
              <Sparkles size={14} className="shrink-0" />
              Real results
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-100 mb-3">See the difference</h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              Drag the slider on each example to compare the original photo with the Photoclear result.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {BEFORE_AFTER_EXAMPLES.map((example) => (
              <div key={example.caption}>
                <BeforeAfterSlider beforeUrl={example.before} afterUrl={example.after} />
                <p className="text-sm text-slate-400 mt-3 text-center">{example.caption}</p>
              </div>
            ))}
          </div>

          <div className="flex justify-center mt-12">
            <button
              onClick={onSignUp}
              className="group flex items-center gap-3 px-10 py-5 bg-gradient-to-r from-blue-600 to-cyan-500 hover:brightness-110 text-white rounded-2xl font-semibold text-lg transition-all shadow-xl shadow-blue-900/40 border border-blue-400/30"
            >
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-blue-600 group-hover:rotate-[360deg] transition-transform duration-700">
                <Sparkles size={20} strokeWidth={2} />
              </span>
              Try PhotoClear Free
            </button>
          </div>
        </div>

        {/* ReHub ecosystem */}
        <div className="max-w-6xl mx-auto mt-28">
          <div className="text-center mb-12">
            <p className="text-sm font-semibold uppercase tracking-widest text-teal-400 mb-4">
              The ReHub Ecosystem
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-100 mb-3">
              One platform. Every property marketing app.
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              Each app is purpose-built for property and works beautifully on its own — or together as one seamless workflow.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {REHUB_APPS.map((app) => (
              <a
                key={app.name}
                href={app.url}
                target={app.current ? undefined : '_blank'}
                rel={app.current ? undefined : 'noopener noreferrer'}
                className="group flex flex-col rounded-2xl border border-slate-700/70 bg-slate-900/50 backdrop-blur-sm p-6 text-left transition-all hover:border-slate-500 hover:bg-slate-900/80"
              >
                <div className={`mb-5 ${ECOSYSTEM_LOGO_SLOT_CLASS}`}>
                  <img
                    src={app.logo}
                    alt={app.name}
                    className={ecosystemLogoClass(app.name)}
                  />
                </div>
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">
                  {app.tagline}
                </p>
                <p className="text-sm text-slate-400 leading-relaxed mb-5 grow">
                  {app.description}
                </p>
                <span className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-200 group-hover:text-white transition-colors">
                  {app.current ? "You're here" : 'Launch app'}
                  {!app.current && (
                    <ArrowUpRight size={16} className="text-slate-500 group-hover:text-teal-300 transition-colors" />
                  )}
                </span>
              </a>
            ))}
          </div>
        </div>
      </div>

      <AskAiSection
        productName={ASK_AI_PRODUCT}
        prompt={ASK_AI_PROMPT}
        providers={ASK_AI_PROVIDERS}
        overviewHref="https://photoclear.ai/ask-ai"
        description="Ask ChatGPT, Perplexity, Grok, Claude or Gemini about PhotoClear’s decluttering tools, pricing and whether it fits your listing workflow."
      />

    </main>
  );
};

export default LandingPage;
